/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import Switch, { Case, Default } from "react-switch-case"
import Loading from "@medaica/common/components/loading"
import { logError } from "@medaica/common/services/util"
import { ThemeProvider } from "@mui/material/styles"
import bookingTheme from "./booking-theme"
import { BookingContextProvider, useBookingStores } from "./booking-context"
import { BookingView } from "./stores/booking-ui-store"
import BookingLayout from "./components/booking-layout"
import Verify from "./views/verify"
import Account from "./views/account"
import Patient from "./views/patient"
import Consent from "./views/consent"
import Health from "./views/health"
import Time from "./views/time"
import EmailEntry from "./views/email-entry"
import Done from "./views/done"

/**
 * Entry routing (05 §1 / D29): `/schedule?t=<token>` starts at the code gate; bare
 * `/schedule` (bookmark / lost link) starts at email entry; an expired/invalid token
 * is resolved server-side and also falls through to email entry (handled below).
 */
const getInitialView = (): BookingView => {
  const token = new URLSearchParams(window.location.search).get("t")
  return token ? "verify" : "emailEntry"
}

const BookingAppInner = observer((): ReactElement => {
  const { uiStore, sessionStore } = useBookingStores()
  const [initializing, setInitializing] = useState(true)

  useEffect(() => {
    const init = async (): Promise<void> => {
      try {
        const token = new URLSearchParams(window.location.search).get("t")
        if (token) {
          // A personal link identifies a specific buyer/order, so it always wins over
          // any session cached in this browser (e.g. a shared/kiosk device) — a second
          // buyer opening their own link never lands on the first buyer's account (08
          // §1). startSession evicts the cached session and routes to the code gate.
          await sessionStore.startSession({ token })
        } else if (sessionStore.isAuthenticated) {
          await sessionStore.loadAccount()
          // loadAccount may find the session already gone (expired server-side) and
          // route to re-verify itself; only land on the account if it survived, so we
          // never strand the user on the Account screen with a null (spinner) account.
          if (sessionStore.isAuthenticated) {
            void sessionStore.loadConfig() // preload the booking window/exam config for the Time step
            uiStore.goTo("account")
          }
        } else {
          uiStore.goTo("emailEntry")
        }
      } catch (error) {
        // An invalid/expired token falls through to the email-entry step (D29).
        logError(error)
        uiStore.goTo("emailEntry")
      } finally {
        setInitializing(false)
      }
    }
    void init()
  }, [sessionStore, uiStore])

  if (initializing) {
    return (
      <BookingLayout>
        <div className="py-24">
          <Loading />
        </div>
      </BookingLayout>
    )
  }

  return (
    <Switch condition={uiStore.view}>
      <Case value="verify">
        <Verify />
      </Case>
      <Case value="emailEntry">
        <EmailEntry />
      </Case>
      <Case value="account">
        <Account />
      </Case>
      <Case value="patient">
        <Patient />
      </Case>
      <Case value="consent">
        <Consent />
      </Case>
      <Case value="health">
        <Health />
      </Case>
      <Case value="time">
        <Time />
      </Case>
      <Case value="done">
        <Done />
      </Case>
      <Default>
        <Verify />
      </Default>
    </Switch>
  )
})

const BookingApp = (): ReactElement => (
  <ThemeProvider theme={bookingTheme}>
    <BookingContextProvider initialView={getInitialView()}>
      <BookingAppInner />
    </BookingContextProvider>
  </ThemeProvider>
)

export default BookingApp
